import React, { useState, useCallback, useEffect, Suspense } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { ConfirmProvider } from "@/contexts/ConfirmContext";
import { CartProvider } from "@/contexts/CartContext";
import { ShopCartProvider } from "@/contexts/ShopCartContext";
import ShopCartPanel from "@/components/shop/ShopCartPanel";
import { AuthProvider } from "@/contexts/AuthContext";
import { PlatformAuthProvider } from "@/contexts/PlatformAuthContext";
import { AudioPlayerProvider } from "@/contexts/AudioPlayerContext";
import AudioPlayer from "@/components/player/AudioPlayer";
import ConvertNudge from "@/components/player/ConvertNudge";
import { ConversionNudgeProvider } from "@/contexts/ConversionNudgeContext";
import { ScrollRestoration } from "@/components/common/ScrollRestoration";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import SplashScreen from "@/components/SplashScreen";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { PlatformWordmark } from "@/components/common/BrandMark";
import { AnnouncementBanner, MaintenanceGate } from "@/components/PublicSiteGates";
import EmailSubscription from "@/components/EmailSubscription";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import CookieConsent from "@/components/CookieConsent";
import Home from "@/pages/Home";
import Landing from "@/pages/Landing";
import { useLaunch } from "@/lib/useLaunch";
import Login from "@/pages/Login";
import ShopperLogin from "@/components/auth/ShopperLogin";
import DonUpgradePopup from "@/components/subscribe/DonUpgradePopup";
import { DmWinbackTeaser } from "@/components/dm/DmWinbackTeaser";
import { DmInbox } from "@/components/dm/DmInbox";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import { HintsProvider } from "@/contexts/HintsContext";
import { PlatformNav } from "@/components/platform/PlatformNav";
import PastDueBanner from "@/components/platform/PastDueBanner";import MembersBanBanner from "@/components/platform/MembersBanBanner";import RenewalCardBanner from "@/components/platform/RenewalCardBanner";import { NotificationBell } from "@/components/platform/NotificationBell";import { TopSearchButton } from "@/components/platform/TopSearchButton";
import PositionOpenBanner from "@/components/positions/PositionOpenBanner";
import RestrictedToast from "@/components/common/RestrictedToast";
import PendingClaimBanner from "@/components/positions/PendingClaimBanner";
import GlobalSearch from "@/components/search/GlobalSearch";
import LiveActivityBanners from "@/components/LiveActivityBanners";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { useAuth } from "@/contexts/AuthContext";
import NotFound from "@/pages/NotFound";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { lazyPage } from "@/lib/lazyPage";
import { PageLoader } from "@/components/common/PageLoader";

const Licensing = lazyPage(() => import("@/pages/Licensing"));
const Community = lazyPage(() => import("@/pages/Community"));
const Blog = lazyPage(() => import("@/pages/Blog"));
const BlogPost = lazyPage(() => import("@/pages/BlogPost"));
const Press = lazyPage(() => import("@/pages/Press"));
const SmartLink = lazyPage(() => import("@/pages/SmartLink"));
const SmartLinkEmbed = lazyPage(() => import("@/pages/SmartLinkEmbed"));
const DeepLink = lazyPage(() => import("@/pages/DeepLink"));
const Contests = lazyPage(() => import("@/pages/Contests"));
const ContestDetail = lazyPage(() => import("@/pages/ContestDetail"));
const Clipping = lazyPage(() => import("@/pages/Clipping"));
const ClippingCampaigns = lazyPage(() => import("@/pages/ClippingCampaigns"));
const ClippingCampaignDetail = lazyPage(() => import("@/pages/ClippingCampaignDetail"));
const ClipperRegister = lazyPage(() => import("@/pages/ClipperRegister"));
const ClipperDashboard = lazyPage(() => import("@/pages/ClipperDashboard"));
const TermsOfService = lazyPage(() => import("@/pages/TermsOfService"));
const AccountBilling = lazyPage(() => import("@/pages/AccountBilling"));
const Music = lazyPage(() => import("@/pages/Music"));
const AlbumDetail = lazyPage(() => import("@/pages/AlbumDetail"));
const TrackDetail = lazyPage(() => import("@/pages/TrackDetail"));
const PlaylistDetail = lazyPage(() => import("@/pages/PlaylistDetail"));
const Watch = lazyPage(() => import("@/pages/Watch"));
const PrivacyPolicy = lazyPage(() => import("@/pages/PrivacyPolicy"));
const RefundPolicy = lazyPage(() => import("@/pages/RefundPolicy"));
const DmcaPolicy = lazyPage(() => import("@/pages/DmcaPolicy"));
const ClipperAgreement = lazyPage(() => import("@/pages/ClipperAgreement"));
const CookiePolicy = lazyPage(() => import("@/pages/CookiePolicy"));
const Unsubscribe = lazyPage(() => import("@/pages/Unsubscribe"));
const AdminPanel = lazyPage(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPanel"));
const RankPreview = lazyPage(() => import(/* webpackChunkName: "admin" */ "@/pages/RankPreview"));
const ConfirmDeviceRemoval = lazyPage(() => import("@/pages/ConfirmDeviceRemoval"));
const ResetPassword = lazyPage(() => import("@/pages/ResetPassword"));
const ConfirmAccount = lazyPage(() => import("@/pages/ConfirmAccount"));
const MagicLogin = lazyPage(() => import("@/pages/MagicLogin"));
const Profile = lazyPage(() => import("@/pages/Profile"));
const Subscribe = lazyPage(() => import("@/pages/Subscribe"));
const Subscribers = lazyPage(() => import("@/pages/Subscribers"));
const VideoDetail = lazyPage(() => import("@/pages/VideoDetail"));
const ShowDetail = lazyPage(() => import("@/pages/ShowDetail"));
const SharePreview = lazyPage(() => import("@/pages/SharePreview"));
const Settings = lazyPage(() => import("@/pages/Settings"));
const Messages = lazyPage(() => import("@/pages/Messages"));
const ComingSoon = lazyPage(() => import("@/pages/ComingSoon"));
const ConfirmArtistEmail = lazyPage(() => import("@/pages/ConfirmArtistEmail"));
const Contact = lazyPage(() => import("@/pages/Contact"));
const Help = lazyPage(() => import("@/pages/Help"));
const Shop = lazyPage(() => import("@/pages/Shop"));
const AccountPurchases = lazyPage(() => import("@/pages/AccountPurchases"));

// Legacy /contests/campaigns/:slug -> /clipping/campaigns/:slug (routes renamed).
const CampaignSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/clipping/campaigns/${slug}`} replace />;
};

// Pre-launch gate: the campaign marketplace and clipper dashboard are closed until
// an admin flips the program live. Guessing the URL redirects to the waitlist page.
const ClipperGate = ({ children }) => {
  const [status, setStatus] = useState("loading"); // loading | live | dark
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clipper/program`)
      .then((r) => setStatus(r.data?.enabled ? "live" : "dark"))
      .catch(() => setStatus("dark"));
  }, []);
  if (status === "loading") return null;
  if (status === "dark") return <Navigate to="/clipping" replace />;
  return children;
};

// Hidden programs (clipper, contests): admin and the artist account only; everyone
// else sees the normal 404 page. The API enforces the same rule.
const OwnerGate = ({ children }) => {
  const { user, loading: adminLoading } = useAuth();
  const { platformUser, loading: platLoading } = usePlatformAuth();
  if (adminLoading || platLoading) return null;
  const owner = user?.role === "admin" || (platformUser?.roles || []).includes("artist");
  return owner ? children : <NotFound />;
};

// Page transitions (ux-polish.md item 145): opacity 0 -> 1, max 150ms, on every route change.
const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeInOut" }}
      >
        <Suspense fallback={<PageLoader />}>
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/licensing" element={<Licensing />} />
          <Route path="/community" element={<Community />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/epk" element={<Navigate to="/press" replace />} />
          <Route path="/press" element={<Press />} />
          <Route path="/clipping" element={<OwnerGate><Clipping /></OwnerGate>} />
          <Route path="/clipping/campaigns" element={<OwnerGate><ClipperGate><ClippingCampaigns /></ClipperGate></OwnerGate>} />
          <Route path="/clipping/campaigns/:slug" element={<OwnerGate><ClipperGate><ClippingCampaignDetail /></ClipperGate></OwnerGate>} />
          <Route path="/contests" element={<OwnerGate><Contests /></OwnerGate>} />
          <Route path="/contests/campaigns/:slug" element={<OwnerGate><CampaignSlugRedirect /></OwnerGate>} />
          <Route path="/contests/:slug" element={<OwnerGate><ContestDetail /></OwnerGate>} />
          <Route path="/clips/register" element={<OwnerGate><ClipperRegister /></OwnerGate>} />
          <Route path="/account/clipper" element={<OwnerGate><ClipperGate><ClipperDashboard /></ClipperGate></OwnerGate>} />
          <Route path="/r/:slug" element={<SmartLink />} />
          <Route path="/embed/r/:slug" element={<SmartLinkEmbed />} />
          <Route path="/s/:type/:id" element={<SharePreview />} />
          <Route path="/d/:slug" element={<DeepLink />} />
          <Route path="/confirm-artist-email" element={<ConfirmArtistEmail />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/dmca" element={<DmcaPolicy />} />
          <Route path="/clipper-agreement" element={<ClipperAgreement />} />
          <Route path="/account/billing" element={<AccountBilling />} />
          <Route path="/account/purchases" element={<AccountPurchases />} />
          <Route path="/music" element={<Music />} />
          <Route path="/music/album/:id" element={<AlbumDetail />} />
          <Route path="/music/track/:id" element={<TrackDetail />} />
          <Route path="/music/playlist/:id" element={<PlaylistDetail />} />
          <Route path="/watch" element={<Watch />} />
          <Route path="/videos/:id" element={<VideoDetail />} />
          <Route path="/shows/:id" element={<ShowDetail />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/u/:username" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/inbox" element={<div className="min-h-screen pt-20 px-4 max-w-6xl mx-auto" style={{ background: "var(--bg-primary)" }}><DmInbox /></div>} />
          <Route path="/subscribers" element={<Subscribers />} />
          <Route path="/login" element={<Login />} />
          <Route path="/confirm-device-removal" element={<ConfirmDeviceRemoval />} />
          <Route path="/shopper-login" element={<ShopperLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-account" element={<ConfirmAccount />} />
          <Route path="/magic-login" element={<MagicLogin />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/s2-console-9f4b" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/home" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/studio" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/shop" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/audience" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/campaigns" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/analytics" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/settings" element={<AdminPanel />} />
          <Route path="/s2-console-9f4b/rank-preview" element={<RankPreview />} />
          {/* Routes whose full pages land in later phases */}
          <Route path="/shop" element={<Shop />} />
          <Route path="/join" element={<ComingSoon title="Join SAMTWO" />} />
          <Route path="/pricing" element={<Subscribe />} />
          <Route path="/subscribe" element={<Navigate to={{ pathname: "/pricing", search: window.location.search }} replace />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const { isSubscriber, isFormerSubscriber, platformUser, loading } = usePlatformAuth();
  const path = location.pathname;

  // Section Visibility (Site Content CMS): gate the site-wide Live Activity
  // Banner. Defaults to visible until the flag loads so nothing flashes hidden.
  const [liveBannerOn, setLiveBannerOn] = useState(true);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/home/section-visibility`)
      .then((r) => setLiveBannerOn(r.data?.live_activity_banner !== false))
      .catch(() => {});
  }, []);

  const isSmartLink = path.startsWith("/r/") || path.startsWith("/embed/r/");
  const isDeepLink = path.startsWith("/d/");
  const isSharePreview = path.startsWith("/s/");
  const isUnsubscribe = path === "/unsubscribe";
  const isAdminRoute = path.startsWith("/s2-console-9f4b");

  const roles = platformUser?.roles || [];
  const launch = useLaunch();
  const launchPrivileged = roles.includes("admin") || roles.includes("samtwo") || roles.includes("artist");
  // Pre-launch, the only pages that reach Layout for a non-privileged visitor are the
  // allowlisted bypass pages (legal, auth). Give them the same minimal shell as the
  // landing: no public nav, no Join/price button, no tip widget, no language/theme
  // controls, only the legal-only footer + a way back. Reverts to the full shell
  // automatically once the site is live. Legal content + cookie control are untouched.
  const gatedMinimal = launch && launch.live === false && !launchPrivileged && !isAdminRoute;
  const isAdminUser = roles.includes("admin");
  const maintenanceBypass = roles.includes("admin") || roles.includes("samtwo");
  // Round 2 16a: PurchaserNav is gone. Everyone gets the one public header +
  // footer (or the platform overlay nav on platform routes). Logout for
  // purchasers/clippers lives on the My Purchases and Clipper Dashboard pages.
  // Profile settings stay reachable by current AND former subscribers (16c).
  const canUseSettings = isSubscriber || isFormerSubscriber || isAdminUser;

  // Route classification (access-tiers.md). Platform routes now show the
  // collapsible overlay nav for EVERYONE (guest/purchaser/subscriber/admin);
  // /settings is subscriber/admin only.
  const everyonePlatform =
    ["/music", "/videos", "/community", "/profile", "/messages"].includes(path) ||
    path.startsWith("/watch") || path.startsWith("/stories") ||
    path.startsWith("/u/") || /^\/music\/.+/.test(path) || /^\/videos\/.+/.test(path);
  const isSettings = path === "/settings";
  const isInbox = path === "/inbox";
  // Clipping flow routes: logged-in subscribers/admins get the account overlay
  // PlatformNav; purchasers/clippers get the public header plus the on-page
  // logout on the Clipper Dashboard; guests keep the public marketing Navigation.
  const isClippingRoute = path === "/clipping" || path.startsWith("/clipping/") ||
    path === "/clips/register" || path === "/account/clipper";
  const isPlatform = everyonePlatform ||
    (isClippingRoute && (isSubscriber || isAdminUser)) ||
    (isSettings && canUseSettings) ||
    (isInbox && (isSubscriber || isAdminUser));

  // Nav decision. The overlay platform nav overlays content (never pushes it) and
  // shows for anyone on platform routes, so there is no public-nav flash to guard.
  const showPlatformNav = isPlatform;
  // Approved platform-only placement. Messages keeps its existing identity row untouched.
  const brandRoute = ["/music", "/community", "/profile", "/settings", "/inbox"].includes(path) ||
    path.startsWith("/u/") || /^\/music\/(album|track|playlist)\//.test(path) ||
    (isClippingRoute && (isAdminUser || roles.includes("artist")));
  const showPlatformWordmark = showPlatformNav && brandRoute;
  const authPending = isSettings && loading && !canUseSettings;
  const showPublicNav = !showPlatformNav && !authPending;
  const showFooter = showPublicNav && !isAdminRoute;
  const isBlog = path === "/blog" || path.startsWith("/blog/");

  // Hold until the launch state is known: the gated and full layouts are different
  // trees, so rendering before it resolves would remount the page (and re-fire its
  // one-shot effects, e.g. the confirm-link POST) once it arrives.
  if (!launch) {
    return <div style={{ minHeight: "100vh", background: "var(--bg-primary)" }} data-testid="layout-launch-pending" />;
  }

  if (gatedMinimal) {
    return (
      <>
        <div className="min-h-screen" style={{ background: "var(--bg-primary)" }}>{children}</div>
        <footer className="py-10 px-4 border-t text-center" style={{ borderColor: "var(--border-color)" }} data-testid="gated-legal-footer">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-4 text-xs" style={{ color: "var(--text-secondary)" }}>
            <a href="/" className="hover:underline" data-testid="gated-footer-home">← SAMTWO</a>
            <a href="/terms" className="hover:underline">Terms</a>
            <a href="/privacy" className="hover:underline">Privacy</a>
            <a href="/cookies" className="hover:underline">Cookies</a>
            <a href="/refund" className="hover:underline">Refund</a>
            <a href="/dmca" className="hover:underline">DMCA</a>
          </div>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>© 2026 SAMTWO</p>
        </footer>
        <CookieConsent />
      </>
    );
  }

  if (isSmartLink || isDeepLink || isSharePreview) {
    return (<>{children}<AudioPlayer /><CookieConsent /></>);
  }

  if (isUnsubscribe) {
    return <>{children}</>;
  }

  return (
    <>
      {showPublicNav && !isAdminRoute && <AnnouncementBanner />}
      {showPublicNav && <Navigation />}
      {showPlatformNav && <PlatformNav />}
      {showPlatformNav && <NotificationBell />}
      {showPlatformNav && <TopSearchButton />}
      {(showPlatformNav || path === "/") && <PositionOpenBanner />}
      <RestrictedToast />
      <PendingClaimBanner />
      {showPlatformNav && <PastDueBanner />}
      {showPlatformNav && <MembersBanBanner />}
      {showPlatformNav && <RenewalCardBanner />}
      {showPlatformNav && <GlobalSearch />}
      {!isAdminRoute && liveBannerOn && <LiveActivityBanners />}
      <AnalyticsTracker />
      <MaintenanceGate bypass={maintenanceBypass || isAdminRoute || path === "/login" || path === "/reset-password" || path === "/confirm-account"}>
        <div className={showPublicNav && !isAdminRoute ? "public-shell" : showPlatformWordmark ? "platform-brand-shell" : undefined}>
          {showPlatformWordmark && <PlatformWordmark />}
          {children}
        </div>
      </MaintenanceGate>
      {showFooter && <Footer />}
      {isBlog && <EmailSubscription />}
      <AudioPlayer />
      <ConvertNudge />
      <ShopCartPanel />
      <CookieConsent wide={showPublicNav && !isAdminRoute} />
      <DonUpgradePopup />
      <DmWinbackTeaser />
      <OnboardingFlow />
    </>
  );
};

// Pre-launch gate (frontend). While the site is not yet live, non-privileged
// visitors see only the landing/countdown page. Legal pages, auth entry points and
// admin remain reachable so artist/admin can operate and legal stays accessible.
// The backend enforces the same gate on the APIs; this is the visitor-facing half.
const LAUNCH_BYPASS = [
  /^\/login/, /^\/reset-password/, /^\/confirm-account/, /^\/magic-login/,
  /^\/unsubscribe/, /^\/confirm-device-removal/, /^\/shopper-login/,
  /^\/terms/, /^\/privacy/, /^\/cookies/, /^\/refund/, /^\/dmca/,
  /^\/clipper-agreement/, /^\/s2-console-9f4b/, /^\/confirm-artist-email/,
];

const LaunchGate = ({ children }) => {
  const location = useLocation();
  const { platformUser } = usePlatformAuth();
  const launch = useLaunch();
  const roles = platformUser?.roles || [];
  const privileged = roles.some((r) => ["admin", "samtwo", "artist"].includes(r));
  const bypass = LAUNCH_BYPASS.some((re) => re.test(location.pathname));
  // Hold a neutral screen until the launch state resolves so the gated site never
  // flashes underneath the landing (and never fires its gated APIs). Bypass routes
  // (auth, legal, admin) render immediately without waiting.
  if (!launch && !bypass) {
    return <div style={{ minHeight: "100vh", background: "var(--bg-primary)" }} data-testid="launch-gate-loading" />;
  }
  if (launch && !launch.live && !privileged && !bypass) {
    return (<><Landing /><CookieConsent /></>);
  }
  return children;
};

function App() {
  const [splashDone, setSplashDone] = useState(() => {
    try {
      if (window.location.pathname.startsWith("/s/")) return true;
      return sessionStorage.getItem("samtwo_splash_seen") === "1";
    } catch { return false; }
  });
  const handleSplashComplete = useCallback(() => {
    try { sessionStorage.setItem("samtwo_splash_seen", "1"); } catch {}
    setSplashDone(true);
  }, []);

  return (
    <LanguageProvider>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <CartProvider>
              <AuthProvider>
                <PlatformAuthProvider>
                  <ShopCartProvider>
                  <ConversionNudgeProvider>
                    <AudioPlayerProvider>
                      {!splashDone && <SplashScreen onComplete={handleSplashComplete} />}
                      <BrowserRouter>
                        <ScrollRestoration />
                        <div className="App" style={{ background: "var(--bg-primary)", minHeight: "100vh", position: "relative" }}>
                          <SonnerToaster richColors closeButton position="top-right" />
                          <HintsProvider>
                            <LaunchGate>
                              <Layout>
                                <ErrorBoundary>
                                  <AnimatedRoutes />
                                </ErrorBoundary>
                              </Layout>
                            </LaunchGate>
                          </HintsProvider>
                        </div>
                      </BrowserRouter>
                    </AudioPlayerProvider>
                  </ConversionNudgeProvider>
                  </ShopCartProvider>
                </PlatformAuthProvider>
              </AuthProvider>
            </CartProvider>
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
